import { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useIsAvaliador } from '@/hooks/useIsAvaliador';
import { useMenuDinamico } from '@/hooks/useMenuDinamico';
import { montarMenuVisivel } from '@/utils/montarMenu';
import type { MenuItemDinamico } from '@/hooks/useMenuDinamico';
import { useSidebar, ESCALAS_LOGO } from '@/contexts/SidebarContext';
import { useActiveUnit } from '@/contexts/ActiveUnitContext';
import { useBrandingUnidade } from '@/hooks/useBrandingUnidade';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, AlertCircle, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { resolverHome } from '@/utils/homeRoute';


const LOGO_PADRAO = '/images/logo_edulumina.webp';

/** Gera iniciais (até 2) a partir de um nome qualquer. */
const gerarIniciais = (nome?: string | null): string => {
  if (!nome) return 'ED';
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return 'ED';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

interface SidebarContentProps {
  /** Quando true, força o modo expandido (usado no drawer mobile). */
  forcarExpandido?: boolean;
  onNavigate?: () => void;
}

export const SidebarContent = ({ forcarExpandido = false, onNavigate }: SidebarContentProps) => {
  const { isAvaliador } = useIsAvaliador();
  const { grupos, isLoading, isError } = useMenuDinamico();
  const { signOut, rolesError, roles } = useAuth();
  const home = resolverHome(roles);

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { colapsada, escalaLogo, cicloEscalaLogo } = useSidebar();
  const compacta = !forcarExpandido && colapsada;
  const { activeUnitId, activeUnitName } = useActiveUnit();
  const { data: branding } = useBrandingUnidade(activeUnitId);

  // Quando a unidade ativa tem logo cadastrada, usamos ela; caso contrário,
  // mostramos a logo institucional padrão do EDULumina.
  const temLogoUnidade = Boolean(branding?.logo_oficial);
  const logoUrl = branding?.logo_oficial ?? LOGO_PADRAO;
  const nomeUnidade = activeUnitName ?? branding?.nome ?? 'EDULumina';
  const logoAlt = temLogoUnidade ? nomeUnidade : 'EDULumina';

  // Estado de erro de carregamento da logo: aciona o fallback textual com
  // iniciais + nome da unidade, em vez de simplesmente trocar pela logo padrão.
  const [logoFalhou, setLogoFalhou] = useState(false);
  useEffect(() => {
    setLogoFalhou(false);
  }, [logoUrl]);

  // Escala da logo (1x, 2x, 3x). Aplicada via transform para preservar a área
  // reservada do cabeçalho — a logo cresce dentro do mesmo container.
  const transformEscala = `scale(${escalaLogo})`;

  // Aplicamos as checagens dinâmicas (ex.: avaliador) num helper isolado para
  // que o sidebar fique apenas com responsabilidade de renderização.
  const gruposVisiveis = useMemo(
    () => montarMenuVisivel(grupos, { avaliador: isAvaliador }),
    [grupos, isAvaliador],
  );

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      onNavigate?.();
    },
    [navigate, onNavigate],
  );

  return (
    <TooltipProvider delayDuration={150}>
      {/* Logo — área reservada com altura fixa; conteúdo perfeitamente centralizado */}
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden border-b border-sidebar-border transition-all duration-300',
          compacta ? 'h-16 px-2' : 'h-24 px-4',
        )}
      >
        <button
          type="button"
          onClick={() => { cicloEscalaLogo(); handleNav(home); }}
          aria-label={`Ir para a home e ajustar zoom da logo (atual ${escalaLogo}x)`}
          className="flex h-full w-full items-center justify-center transition-transform duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          style={{ transform: transformEscala, transformOrigin: 'center center' }}
        >
          {logoFalhou ? (
            <LogoFallback nome={nomeUnidade} compacta={compacta} />
          ) : (
            <img
              key={logoUrl}
              src={logoUrl}
              alt={logoAlt}
              decoding="async"
              className={cn(
                'block object-contain transition-all duration-300',
                compacta
                  ? 'max-h-10 max-w-[2.5rem]'
                  : 'max-h-[4.5rem] max-w-[12rem]',
              )}
              onError={() => setLogoFalhou(true)}
            />
          )}
        </button>
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-1', compacta ? 'px-2' : 'px-3')}>
        {isLoading && (
          <div className="space-y-3 px-1">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <Skeleton className="h-4 w-4 shrink-0 rounded" />
                {!compacta && <Skeleton className="h-4 w-full rounded" />}
              </div>
            ))}
          </div>
        )}

        {isError || rolesError ? (
          <div className={cn("flex flex-col items-center justify-center p-4 text-center", compacta ? "px-1" : "px-3")}>
            <AlertCircle className="h-6 w-6 text-destructive mb-2" />
            {!compacta && (
              <>
                <p className="text-xs font-medium text-destructive mb-3">Falha ao carregar menu</p>
                <div className="flex flex-col gap-2 w-full">
                  <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="h-8 text-[11px]">
                    Tentar Novamente
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => signOut()} className="h-8 text-[11px] text-muted-foreground">
                    <LogOut className="h-3 w-3 mr-1" />
                    Sair
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : !isLoading && gruposVisiveis.map((group) => {
          if (!group.label) {
            return (
              <div key="principal" className="space-y-1">
                {group.items.map((item) => (
                  <NavButton
                    key={item.path}
                    item={item}
                    active={pathname === item.path}
                    compacta={compacta}
                    onClick={handleNav}
                  />
                ))}
              </div>
            );
          }

          const groupActive = group.items.some((it) => pathname === it.path);

          // No modo compacto não exibimos o cabeçalho do grupo nem o accordion:
          // os itens ficam sempre visíveis como uma faixa de ícones.
          if (compacta) {
            return (
              <div key={group.label} className="space-y-0.5 pt-1 first:pt-0">
                {group.items.map((item) => (
                  <NavButton
                    key={item.path}
                    item={item}
                    active={pathname === item.path}
                    compacta
                    onClick={handleNav}
                  />
                ))}
              </div>
            );
          }

          return (
            <Collapsible
              key={group.label}
              defaultOpen={groupActive}
              className="space-y-1"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80 hover:bg-sidebar-accent/30 transition-colors duration-200">
                <span className="truncate">{group.label}</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-0.5 overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                {group.items.map((item) => (
                  <NavButton
                    key={item.path}
                    item={item}
                    active={pathname === item.path}
                    compacta={false}
                    onClick={handleNav}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 text-center border-t border-sidebar-border">
        <p className="text-[10px] text-muted-foreground/50 truncate">
          {compacta ? 'v1.0.0' : 'v1.0.0 · EDULumina'}
        </p>
      </div>
    </TooltipProvider>
  );
};

/** Sidebar desktop (hidden on mobile) */
export const AppSidebar = () => {
  const { colapsada } = useSidebar();
  return (
    <aside
      className={cn(
        'hidden md:flex h-screen flex-col bg-sidebar border-r border-sidebar-border',
        'transition-[width] duration-300 ease-out',
        colapsada ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent />
    </aside>
  );
};

interface NavButtonProps {
  item: MenuItemDinamico;
  active: boolean;
  compacta: boolean;
  onClick: (path: string) => void;
}

/**
 * Descrições curtas para itens do grupo "Portais". Renderizadas como subtítulo
 * no modo expandido e no tooltip do modo compacto, para reforçar a navegação.
 */
const DESCRICOES_PORTAIS: Record<string, string> = {
  '/portal/professor': 'Suas turmas, planos e diários',
  '/portal/aluno': 'Notas, frequência e materiais',
  '/portal/coordenacao': 'Indicadores e gestão acadêmica',
};

// memo + onClick estável => somente o botão ativo/anteriormente-ativo re-renderiza ao navegar.
const NavButton = memo(({ item, active, compacta, onClick }: NavButtonProps) => {
  const Icon = item.icon;
  const descricao = DESCRICOES_PORTAIS[item.path];

  const botao = (
    <button
      onClick={() => onClick(item.path)}
      title={compacta ? undefined : item.label}
      aria-label={item.label}
      className={cn(
        'flex w-full items-center rounded-lg text-sm font-medium transition-all duration-200',
        compacta ? 'justify-center px-0 py-2.5 h-10' : 'gap-3 px-3 py-2.5',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-sm ring-1 ring-sidebar-border'
          : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!compacta && (
        <span className="min-w-0 flex-1 text-left">
          <span className="block truncate whitespace-nowrap">{item.label}</span>
          {descricao && (
            <span className="block truncate text-[10.5px] font-normal text-muted-foreground/80">
              {descricao}
            </span>
          )}
        </span>
      )}
    </button>
  );

  if (!compacta) return botao;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{botao}</TooltipTrigger>
      <TooltipContent side="right" className="text-xs">
        <div className="font-medium">{item.label}</div>
        {descricao && (
          <div className="text-[11px] text-muted-foreground">{descricao}</div>
        )}
      </TooltipContent>
    </Tooltip>
  );
});
NavButton.displayName = 'NavButton';

/**
 * Fallback visual exibido quando a logo (da unidade ou padrão) falha em
 * carregar. Mostra um badge com as iniciais e o nome da unidade, mantendo a
 * identidade visual do espaço reservado.
 */
const LogoFallback = ({ nome, compacta }: { nome: string; compacta: boolean }) => {
  const iniciais = gerarIniciais(nome);
  return (
    <div className="flex h-full w-full items-center justify-center gap-2">
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg bg-primary/10 font-bold text-primary ring-1 ring-primary/20',
          compacta ? 'h-9 w-9 text-xs' : 'h-12 w-12 text-base',
        )}
        aria-hidden="true"
      >
        {iniciais}
      </div>
      {!compacta && (
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-sidebar-foreground" title={nome}>
          {nome}
        </span>
      )}
    </div>
  );
};
